import { Fragment, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { DoorOpen } from "lucide-react";

import type { EchoflicksSessionEvent } from "../types";
import { CountCheckLine, CoverageSummary } from "./FloorPlanCard";
import {
  countCheckEntries,
  formatMs,
  furnishingsFor,
  humanizeLevelKind,
  humanizeRoomMapSource,
  propertyTypeSummary,
  roomMapCoverageView,
  roomMapDiagnosticChips,
  roomMapFromEvents,
  roomMapLevelSummaries,
  roomMapLevelWarnings,
  roomMapPhotoRows,
} from "./sessionUtils";

// ---------------------------------------------------------------------------
// RoomMapCard — the director skill's photo→room assignment (FPR-021/033,
// `director.skill_room_map`, `room_map.v1`): how many photos landed in a room,
// how many rooms got photos, the mapped/plan/listing bedroom check, the
// validator's diagnostics, and the per-photo table with the model's evidence.
// Admin Debug column; renders nothing when the session has no such event.
//
// `floor_plan.v2` (FPV-031) adds the property-type chip in the header, a Level
// column once photos carry one, and the furnishings the orchestrator read off
// each room's photos. All optional — a v1 payload renders exactly as before.
//
// BLD-043 adds a per-level summary (rooms, photos placed, kind, storey,
// building) whenever the payload carries levels, and warnings for what used
// to fail silently (plan §1.2): a level with 0 photos placed while photos
// stay unassigned, and unassigned photos with upper-floor cues. Levels and
// rooms the plan does not show (`source: "inferred"`, `x-` room ids) get a
// "not on the plan" badge; the bedroom check gains the inferred count.
// ---------------------------------------------------------------------------

const stat = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) ? String(value) : "—";

const NotOnPlanBadge = () => (
  <Badge
    data-testid="room-map-not-on-plan"
    variant="outline"
    className="ml-1 border-violet-500 px-1 py-0 text-[10px] text-violet-600"
    title="Inferred from the listing: the floor plan does not show this level or room"
  >
    not on the plan
  </Badge>
);

export const RoomMapCard = ({
  events,
}: {
  events: EchoflicksSessionEvent[];
}) => {
  const payload = useMemo(() => roomMapFromEvents(events), [events]);
  if (!payload) return null;

  const rows = roomMapPhotoRows(payload);
  const chips = roomMapDiagnosticChips(payload.diagnostics);
  const checks = countCheckEntries(payload.count_check);
  const property = propertyTypeSummary(payload.property_type);
  const hasLevels = rows.some((row) => row.level !== null);
  const columnCount = hasLevels ? 5 : 4;
  // Furnishings belong to the room, not the photo: show them once, on the
  // first row of each room (the rows are already grouped by room name).
  const furnishingsSeen = new Set<string>();
  const fallback = payload.source === "floor_plan_llm_fallback";
  const attempts = Array.isArray(payload.attempts)
    ? payload.attempts.filter((item) => typeof item === "string")
    : [];
  const problems = Array.isArray(payload.remaining_problems)
    ? payload.remaining_problems.filter((item) => typeof item === "string")
    : [];
  // BLD-043: per-level summary + warnings (empty on a v1 payload).
  const levels = roomMapLevelSummaries(payload);
  const warnings = roomMapLevelWarnings(payload);
  const coverage = roomMapCoverageView(payload);
  const unassignedCount =
    typeof payload.unassigned === "number" ? payload.unassigned : 0;
  const showKind = levels.some((level) => level.kind !== null);
  const showStorey = levels.some((level) => level.storey !== null);
  const showRooms = levels.some((level) => level.rooms !== null);
  const showBuilding =
    new Set(levels.map((level) => level.buildingId ?? "B0")).size > 1;

  return (
    <Card data-testid="room-map-card">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="flex items-center gap-2">
            <DoorOpen className="size-4" />
            Room map
          </CardTitle>
          <Badge
            variant="outline"
            className={fallback ? "border-amber-500 text-amber-600" : ""}
          >
            {humanizeRoomMapSource(payload.source)}
          </Badge>
          {payload.model ? (
            <Badge variant="outline">{payload.model}</Badge>
          ) : null}
          {property ? (
            <Badge
              data-testid="room-map-property-type"
              variant="outline"
              className="border-sky-500 text-sky-600"
              title={[
                `Property type: ${property.label}`,
                property.source ? `from ${property.source}` : null,
                property.confidencePercent,
                ...property.evidence,
              ]
                .filter(Boolean)
                .join(" · ")}
            >
              {property.label}
              {property.confidencePercent
                ? ` · ${property.confidencePercent}`
                : ""}
            </Badge>
          ) : null}
          {typeof payload.elapsed_ms === "number" ? (
            <span className="text-xs text-muted-foreground">
              {formatMs(payload.elapsed_ms)}
            </span>
          ) : null}
          {payload.version ? (
            <span className="ml-auto font-mono text-xs text-muted-foreground">
              {payload.version}
            </span>
          ) : null}
        </div>
        <CardDescription>
          Which room each listing photo was assigned to, from the floor plan the
          director skill read.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <dl className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <div>
            <dt className="text-muted-foreground">Photos</dt>
            <dd className="font-medium">{stat(payload.photos)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Assigned</dt>
            <dd className="font-medium">{stat(payload.assigned)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Unassigned</dt>
            <dd className="font-medium">{stat(payload.unassigned)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Exterior</dt>
            <dd className="font-medium">{stat(payload.exterior)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Rooms with photos</dt>
            <dd className="font-medium">
              {stat(payload.rooms_with_photos)} / {stat(payload.rooms_total)}
            </dd>
          </div>
        </dl>

        <CountCheckLine entries={checks} testId="room-map-count-check" />

        {warnings.length > 0 ? (
          <ul
            data-testid="room-map-warnings"
            className="space-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          >
            {warnings.map((warning) => (
              <li key={warning.key} data-testid="room-map-warning">
                ⚠ {warning.label}
                {warning.detail.length > 0 ? (
                  <span className="block pl-4 opacity-80">
                    {warning.detail.join(" · ")}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        {coverage ? (
          <CoverageSummary coverage={coverage} testId="room-map-coverage" />
        ) : null}

        {levels.length > 0 ? (
          <div
            data-testid="room-map-levels"
            className="overflow-x-auto rounded-md border"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                  <th className="px-2 py-1 font-medium">Level</th>
                  {showKind ? (
                    <th className="px-2 py-1 font-medium">Kind</th>
                  ) : null}
                  {showStorey ? (
                    <th className="px-2 py-1 text-right font-medium">Storey</th>
                  ) : null}
                  {showBuilding ? (
                    <th className="px-2 py-1 font-medium">Building</th>
                  ) : null}
                  {showRooms ? (
                    <th className="px-2 py-1 text-right font-medium">Rooms</th>
                  ) : null}
                  <th className="px-2 py-1 text-right font-medium">
                    Photos placed
                  </th>
                </tr>
              </thead>
              <tbody>
                {levels.map((level) => {
                  const empty = level.photos === 0 && unassignedCount > 0;
                  return (
                    <tr
                      key={level.levelId}
                      data-testid="room-map-level"
                      data-empty={empty ? "true" : undefined}
                      className={`border-b last:border-b-0 ${empty ? "bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-200" : ""}`}
                    >
                      <td className="px-2 py-1">
                        <span className="font-medium">{level.label}</span>
                        {level.ordinal !== null ? (
                          <span className="ml-1 text-muted-foreground">
                            level {level.ordinal}
                          </span>
                        ) : null}
                        {level.inferred ? <NotOnPlanBadge /> : null}
                      </td>
                      {showKind ? (
                        <td className="px-2 py-1">
                          {humanizeLevelKind(level.kind) ?? ""}
                        </td>
                      ) : null}
                      {showStorey ? (
                        <td className="px-2 py-1 text-right">
                          {level.storey ?? ""}
                        </td>
                      ) : null}
                      {showBuilding ? (
                        <td className="px-2 py-1 font-mono">
                          {level.buildingId ?? "B0"}
                        </td>
                      ) : null}
                      {showRooms ? (
                        <td className="px-2 py-1 text-right">
                          {level.rooms ?? ""}
                        </td>
                      ) : null}
                      <td className="px-2 py-1 text-right font-medium">
                        {level.photos ?? "?"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {chips.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {chips.map((chip) => (
              <Badge
                key={chip.key}
                variant="outline"
                className={
                  chip.warn
                    ? "border-amber-500 text-amber-600"
                    : "text-muted-foreground"
                }
              >
                {chip.label}
              </Badge>
            ))}
          </div>
        ) : null}

        {attempts.length > 0 ? (
          <div className="text-xs text-muted-foreground">
            Attempts: {attempts.join(" → ")}
          </div>
        ) : null}

        {problems.length > 0 ? (
          <ul className="list-disc pl-5 text-xs text-amber-700 dark:text-amber-300">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        ) : null}

        {rows.length > 0 ? (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                  <th className="px-2 py-1 font-medium">File</th>
                  <th className="px-2 py-1 font-medium">Room</th>
                  {hasLevels ? (
                    <th className="px-2 py-1 font-medium">Level</th>
                  ) : null}
                  <th className="px-2 py-1 text-right font-medium">Conf.</th>
                  <th className="px-2 py-1 font-medium">Evidence</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const furnishings =
                    row.roomId && !furnishingsSeen.has(row.roomId)
                      ? furnishingsFor(payload, row.roomId)
                      : [];
                  if (row.roomId) furnishingsSeen.add(row.roomId);
                  return (
                    <Fragment key={row.file}>
                      <tr
                        className={
                          furnishings.length > 0
                            ? ""
                            : "border-b last:border-b-0"
                        }
                      >
                        <td className="max-w-[14rem] truncate px-2 py-1 font-mono">
                          {row.file}
                        </td>
                        <td className="px-2 py-1">
                          <span className="font-medium">{row.room}</span>
                          {row.roomId ? (
                            <span className="ml-1 text-muted-foreground">
                              {row.roomId}
                            </span>
                          ) : null}
                          {row.inferred ? <NotOnPlanBadge /> : null}
                        </td>
                        {hasLevels ? (
                          <td className="px-2 py-1">{row.level ?? "—"}</td>
                        ) : null}
                        <td className="px-2 py-1 text-right">
                          {row.confidence}
                        </td>
                        <td className="px-2 py-1 text-muted-foreground">
                          {row.evidence || "—"}
                        </td>
                      </tr>
                      {furnishings.length > 0 ? (
                        <tr
                          data-testid="room-map-furnishings"
                          className="border-b last:border-b-0"
                        >
                          <td />
                          <td
                            colSpan={columnCount - 1}
                            className="px-2 pb-1 text-muted-foreground"
                          >
                            {furnishings.join(" · ")}
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            No per-photo assignments in this event.
          </div>
        )}
      </CardContent>
    </Card>
  );
};
